import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { UserPlus, MessageCircle, Contact } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { useUI } from "@/components/Layout";

export default function RightSidebar() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { openChat, openContacts } = useUI();
  const { data: suggestions = [] } = useQuery({
    queryKey: ["suggestions"],
    queryFn: async () => (await api.get("/users/suggestions")).data,
  });
  const { data: friends = [] } = useQuery({
    queryKey: ["friends"],
    queryFn: async () => (await api.get("/friends")).data,
  });

  const addFriend = async (id) => {
    try {
      await api.post(`/friends/request/${id}`);
      toast.success("Friend request sent");
      qc.invalidateQueries({ queryKey: ["suggestions"] });
      qc.invalidateQueries({ queryKey: ["friends"] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-4">
      <button
        data-testid="sync-contacts-btn"
        onClick={openContacts}
        className="w-full flex items-center gap-3 p-3.5 rounded-2xl border-2 border-[#6C0BA9]/40 bg-gradient-to-br from-[#6C0BA9]/15 to-[#E024A3]/10 hover:border-[#6C0BA9] transition-all text-left group"
      >
        <span className="w-10 h-10 rounded-xl bg-[#6C0BA9]/20 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
          <Contact className="w-5 h-5 text-[#C084FC]" />
        </span>
        <div>
          <div className="font-heading font-bold text-sm">Find Friends</div>
          <div className="text-xs text-muted-foreground">Sync your phone contacts</div>
        </div>
      </button>

      {suggestions.length > 0 && (
        <div className="bg-card border border-border rounded-2xl p-4">
          <h3 className="font-heading font-bold text-sm mb-3 text-muted-foreground uppercase tracking-wide">People you may know</h3>
          <div className="space-y-1">
            {suggestions.map((u) => (
              <div key={u.user_id} className="flex items-center gap-2 py-1.5" data-testid={`suggestion-${u.user_id}`}>
                <button onClick={() => navigate(`/profile/${u.user_id}`)} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                  <Avatar user={u} size={36} />
                  <span className="text-sm font-medium truncate flex items-center gap-1">{u.name}{u.verified && <VerifiedBadge size={12} />}</span>
                </button>
                <button data-testid={`add-friend-${u.user_id}`} onClick={() => addFriend(u.user_id)} className="w-8 h-8 rounded-full bg-[#6C0BA9]/15 text-[#C084FC] hover:bg-[#6C0BA9]/30 flex items-center justify-center transition-colors">
                  <UserPlus className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-2xl p-4">
        <h3 className="font-heading font-bold text-sm mb-3 text-muted-foreground uppercase tracking-wide">Contacts</h3>
        {friends.length === 0 ? (
          <p className="text-xs text-muted-foreground">Add friends to start chatting.</p>
        ) : (
          <div className="space-y-1">
            {friends.map((u) => (
              <button
                key={u.user_id}
                data-testid={`contact-${u.user_id}`}
                onClick={() => openChat(u)}
                className="w-full flex items-center gap-2 py-1.5 px-1 rounded-xl hover:bg-accent transition-colors text-left"
              >
                <div className="relative">
                  <Avatar user={u} size={34} />
                  <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-card" />
                </div>
                <span className="text-sm font-medium truncate flex items-center gap-1 flex-1">{u.name}{u.verified && <VerifiedBadge size={12} />}</span>
                <MessageCircle className="w-4 h-4 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
